/**
 * QuitScreenAdmin — admin-only panel to configure the farewell screen that
 * appears when the user confirms they want to quit the app.
 *
 * Lets the admin:
 *  - Toggle the quit screen on/off (off = exit immediately)
 *  - Set the countdown duration (3-10 seconds)
 *  - Pick the content mode: thankyou / photo / carousel / ad
 *  - Edit the message + submessage
 *  - Upload/delete photos (used by photo + carousel modes)
 */
import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Upload, Trash2, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

const MODE_OPTIONS = [
  { id: 'thankyou', label: '🙏 Remerciement', tip: 'Message texte uniquement' },
  { id: 'photo',    label: '🖼 Photo',         tip: 'Une seule photo personnalisée' },
  { id: 'carousel', label: '🎞 Carrousel',     tip: 'Plusieurs photos qui défilent' },
  { id: 'ad',       label: '📣 Publicité',     tip: 'AdMob Interstitial (Android uniquement)' },
];

export default function QuitScreenAdmin({ onClose }) {
  const [qs, setQs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/app-settings`);
        setQs(r.data?.quit_screen || {});
      } catch (e) {
        toast.error('Chargement des réglages échoué');
      }
    })();
  }, []);

  const update = async (patch) => {
    setSaving(true);
    const next = { ...(qs || {}), ...patch };
    setQs(next); // optimistic
    try {
      const r = await axios.put(`${API}/api/app-settings`, { quit_screen: patch });
      setQs(r.data?.quit_screen || next);
    } catch (e) {
      toast.error('Mise à jour échouée');
    } finally {
      setSaving(false);
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Image uniquement'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Max 5 Mo'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file, file.name);
      await axios.post(`${API}/api/app-settings/quit-screen/photos/upload`, fd);
      const r = await axios.get(`${API}/api/app-settings`);
      setQs(r.data?.quit_screen || qs);
      toast.success('Photo ajoutée ✓');
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Upload échoué';
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const onDeletePhoto = async (photoId) => {
    if (!window.confirm('Supprimer cette photo ?')) return;
    try {
      await axios.delete(`${API}/api/app-settings/quit-screen/photos/${photoId}`);
      const r = await axios.get(`${API}/api/app-settings`);
      setQs(r.data?.quit_screen || qs);
      toast.success('Photo supprimée');
    } catch {
      toast.error('Suppression échouée');
    }
  };

  if (!qs) {
    return (
      <div className="fixed inset-0 z-[9000] bg-slate-950 flex items-center justify-center" data-testid="quit-screen-admin">
        <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
      </div>
    );
  }

  const photos = qs.photos || [];

  return (
    <div className="fixed inset-0 z-[9000] bg-slate-950 overflow-y-auto" data-testid="quit-screen-admin"
      style={{ transform: 'translateZ(0)', isolation: 'isolate' }}>
      <div className="sticky top-0 z-10 flex items-center justify-between px-5 pt-10 pb-4 bg-slate-950 border-b border-amber-500/15">
        <h2 className="text-lg font-semibold text-amber-300" style={{ fontFamily: "'Playfair Display', serif" }}>
          Écran de sortie (Admin)
        </h2>
        <button onClick={onClose} className="text-white/60 hover:text-white p-2" data-testid="quit-screen-admin-close">
          <X size={22} />
        </button>
      </div>

      <div className="p-5 pb-24 space-y-5 max-w-lg mx-auto">
        {/* Enabled toggle */}
        <div className="flex items-center justify-between bg-white/5 rounded-xl p-3">
          <div>
            <p className="text-white text-sm font-medium">{'Activer l\'écran de sortie'}</p>
            <p className="text-white/50 text-xs">{'Si désactivé, l\'app se ferme immédiatement après "Quitter".'}</p>
          </div>
          <button
            onClick={() => update({ enabled: !qs.enabled })}
            disabled={saving}
            className={`relative w-12 h-6 rounded-full transition ${qs.enabled ? 'bg-amber-500' : 'bg-slate-700'}`}
            data-testid="quit-enabled-toggle"
          >
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${qs.enabled ? 'translate-x-6' : ''}`} />
          </button>
        </div>

        {/* Countdown slider */}
        <div className="bg-white/5 rounded-xl p-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-white text-sm font-medium">Durée du compte à rebours</p>
            <span className="text-amber-300 font-mono text-sm" data-testid="quit-countdown-value">{qs.countdown_seconds}s</span>
          </div>
          <input
            type="range" min="3" max="10" step="1"
            value={qs.countdown_seconds || 5}
            onChange={(e) => update({ countdown_seconds: parseInt(e.target.value, 10) })}
            disabled={saving || !qs.enabled}
            className="w-full accent-amber-400"
            data-testid="quit-countdown-slider"
          />
        </div>

        {/* Mode picker */}
        <div className="space-y-1.5">
          <p className="text-white text-sm font-medium mb-1">Contenu affiché</p>
          {MODE_OPTIONS.map((m) => {
            const active = qs.mode === m.id;
            return (
              <button
                key={m.id}
                onClick={() => update({ mode: m.id })}
                disabled={saving || !qs.enabled}
                className={`w-full text-left px-3 py-2.5 rounded-lg border transition flex items-center justify-between ${active ? 'bg-amber-500/20 border-amber-400/60' : 'bg-slate-900/70 border-white/10 hover:bg-slate-800'}`}
                data-testid={`quit-mode-${m.id}`}
              >
                <div>
                  <p className={`text-sm font-medium ${active ? 'text-amber-100' : 'text-white/85'}`}>{m.label}</p>
                  <p className="text-white/45 text-xs">{m.tip}</p>
                </div>
                <div className={`w-3 h-3 rounded-full border-2 ${active ? 'border-amber-300 bg-amber-300' : 'border-white/25'}`} />
              </button>
            );
          })}
        </div>

        {/* Message + submessage */}
        <div className="bg-white/5 rounded-xl p-3 space-y-2">
          <div>
            <label className="text-white/60 text-xs">Titre</label>
            <input
              type="text"
              value={qs.message || ''}
              onChange={(e) => setQs({ ...qs, message: e.target.value })}
              onBlur={() => update({ message: qs.message })}
              disabled={!qs.enabled}
              className="w-full mt-1 px-3 py-2 bg-slate-900 border border-white/10 rounded-lg text-white text-sm focus:border-amber-400 focus:outline-none"
              data-testid="quit-message-input"
              maxLength={80}
            />
          </div>
          <div>
            <label className="text-white/60 text-xs">Sous-titre</label>
            <textarea
              value={qs.submessage || ''}
              onChange={(e) => setQs({ ...qs, submessage: e.target.value })}
              onBlur={() => update({ submessage: qs.submessage })}
              disabled={!qs.enabled}
              rows={2}
              className="w-full mt-1 px-3 py-2 bg-slate-900 border border-white/10 rounded-lg text-white text-sm focus:border-amber-400 focus:outline-none"
              data-testid="quit-submessage-input"
              maxLength={200}
            />
          </div>
        </div>

        {/* Photo manager (visible for photo + carousel modes) */}
        {(qs.mode === 'photo' || qs.mode === 'carousel') && (
          <div className="bg-white/5 rounded-xl p-3 space-y-2" data-testid="quit-photo-manager">
            <div className="flex items-center justify-between">
              <p className="text-white text-sm font-medium">Photos ({photos.length}/10)</p>
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading || photos.length >= 10}
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-amber-500/20 border border-amber-400/40 text-amber-200 hover:bg-amber-500/30 disabled:opacity-50"
                data-testid="quit-photo-upload-btn"
              >
                {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                Ajouter
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onUpload}
                data-testid="quit-photo-upload-input"
              />
            </div>
            {photos.length === 0 ? (
              <p className="text-white/45 text-xs text-center py-4">{'Aucune photo. Ajoutez-en jusqu\'à 10.'}</p>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {photos.map((p) => (
                  <div key={p.id} className="relative aspect-square rounded-lg overflow-hidden border border-white/10">
                    <img
                      src={`data:${p.mime || 'image/jpeg'};base64,${p.data_b64}`}
                      alt={p.name}
                      className="w-full h-full object-cover"
                    />
                    <button
                      onClick={() => onDeletePhoto(p.id)}
                      className="absolute top-1 right-1 p-1 rounded-full bg-black/60 text-red-300 hover:bg-red-500/30"
                      data-testid={`quit-photo-delete-${p.id}`}
                    >
                      <Trash2 size={11} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {qs.mode === 'ad' && (
          <div className="rounded-xl border border-amber-400/30 bg-amber-500/10 p-3 text-amber-200 text-xs">
            {'ℹ La publicité AdMob Interstitial s\'affichera uniquement sur l\'APK Android avec AdMob configuré. Sur le web, le compte à rebours s\'affiche seul.'}
          </div>
        )}

        <p className="text-white/45 text-[10px] leading-snug pt-2">
          {'Cet écran apparaît après que l\'utilisateur a confirmé "Quitter" sur la home page (touche retour Android). Pendant le compte à rebours, l\'utilisateur peut annuler avec le bouton "Annuler".'}
        </p>
      </div>
    </div>
  );
}
